import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  type Connection,
  type Edge,
  type Node,
  type NodeChange,
  type EdgeChange,
  type ReactFlowInstance,
  MarkerType,
} from '@xyflow/react';
import { nanoid } from 'nanoid';

import type { FlowData, FlowNode, FlowEdge, Workflow, ToastMessage, TestRunEvent, NodeKind } from './types';
import { Toolbar } from './components/Toolbar';
import { Palette } from './components/Palette';
import { PropertiesPanel } from './components/PropertiesPanel';
import { Toasts } from './components/Toast';
import { TestRunDrawer } from './components/TestRunDrawer';
import { LoadingScreen } from './components/LoadingScreen';
import { Tutorial } from './components/Tutorial';
import { MobileBottomNav } from './components/MobileBottomNav';
import { AssistantChat } from './components/AssistantChat';
import { CanvasBackground } from './components/CanvasBackground';
import { ContextMenu, type ContextMenuPosition } from './components/ContextMenu';
import { ZoomWidget } from './components/ZoomWidget';
import { ConfirmDialog } from './components/ConfirmDialog';
import { ShortcutsOverlay } from './components/ShortcutsOverlay';
import { EmptyCanvas } from './components/EmptyCanvas';
import { NodeSearch } from './components/NodeSearch';
import { useConfirm } from './lib/useConfirm';
import { useHistory } from './lib/useHistory';
import { validateNodes } from './lib/nodeValidation';
import { runAiSmart } from './lib/aiBuilder';
import { recordRun } from './lib/runHistory';
import { nanoid as nano } from 'nanoid';
import { buildTutorialSteps } from './lib/tutorialSteps';
import { TriggerNode } from './components/nodes/TriggerNode';
import { WaitNode } from './components/nodes/WaitNode';
import { ConditionNode } from './components/nodes/ConditionNode';
import { ActionNode } from './components/nodes/ActionNode';
import { AiSmartNode } from './components/nodes/AiSmartNode';
import {
  emptyWorkflow,
  templateStormReactivation,
} from './lib/templates';
import {
  saveWorkflow,
  listWorkflows,
  exportWorkflow,
  importWorkflow,
  getActiveWorkflowId,
  setActiveWorkflowId,
} from './lib/storage';

const NODE_TYPES = {
  trigger:   TriggerNode,
  wait:      WaitNode,
  condition: ConditionNode,
  action:    ActionNode,
  ai_smart:  AiSmartNode,
};

const TUTORIAL_DONE_KEY = 'iiq.workflow-builder.tutorial-done.v1';
const SIDEBAR_PALETTE_KEY = 'iiq.workflow-builder.palette-open.v1';
const SIDEBAR_PROPS_KEY = 'iiq.workflow-builder.props-open.v1';

function defaultDataFor(kind: NodeKind): FlowData {
  if (kind === 'trigger') {
    return { kind: 'trigger', source: 'call.missed', label: 'New trigger', description: 'Click to configure' };
  }
  if (kind === 'wait') {
    return { kind: 'wait', label: 'Wait', amount: 1, unit: 'minutes' };
  }
  if (kind === 'condition') {
    return { kind: 'condition', label: 'New condition', field: 'customer.last_serviced_months', operator: 'gte', value: '12' };
  }
  if (kind === 'ai_smart') {
    return {
      kind: 'ai_smart',
      label: 'AI step',
      model: '@cf/meta/llama-3.1-8b-instruct-fast',
      prompt: 'Summarize the customer\'s recent activity in 2 sentences. Customer: {{customer.first_name}} {{customer.last_name}}, balance ${{invoice.balance}}.',
      format: 'text',
      storeAs: 'ai_summary',
      description: 'AI inference at runtime',
    };
  }
  return { kind: 'action', type: 'sms.send', label: 'New action', body: 'Hey {{customer.first_name}} — quick note from the team.', to: '{{customer.phone}}' };
}

function loadInitial(): Workflow {
  const activeId = getActiveWorkflowId();
  if (activeId) {
    const found = listWorkflows().find(w => w.id === activeId);
    if (found) return found;
  }
  return templateStormReactivation();
}

function readBoolStorage(key: string, def: boolean): boolean {
  const v = localStorage.getItem(key);
  if (v === null) return def;
  return v === '1';
}

function isMobile(): boolean {
  return window.matchMedia('(max-width: 820px)').matches;
}

function AppInner() {
  const initial = useMemo(loadInitial, []);
  const [workflow, setWorkflow] = useState<Workflow>(initial);
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>(initial.edges);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<number | null>(initial.updatedAt);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [testEvents, setTestEvents] = useState<TestRunEvent[]>([]);
  const [testVisible, setTestVisible] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Loading + tutorial + AI assistant state
  const [loading, setLoading] = useState(true);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);

  // Right-click context menu
  const [ctxMenu, setCtxMenu] = useState<ContextMenuPosition | null>(null);
  const [clipboard, setClipboard] = useState<FlowNode | null>(null);

  // AI FAB size preference (compact by default)
  const [fabExpanded, setFabExpanded] = useState(() => localStorage.getItem('iiq.workflow-builder.fab-expanded.v1') === '1');
  useEffect(() => {
    localStorage.setItem('iiq.workflow-builder.fab-expanded.v1', fabExpanded ? '1' : '0');
  }, [fabExpanded]);

  // Theme (dark default, persisted) — applied as data-theme on <html>
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    const stored = localStorage.getItem('iiq.workflow-builder.theme.v1');
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('iiq.workflow-builder.theme.v1', theme);
  }, [theme]);
  const toggleTheme = useCallback(() => setTheme(t => (t === 'dark' ? 'light' : 'dark')), []);

  // Themed confirm modal
  const { confirm, request: confirmReq, resolve: resolveConfirm } = useConfirm();

  // Keyboard shortcuts overlay
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Node search overlay (Cmd+F)
  const [searchOpen, setSearchOpen] = useState(false);

  // Minimap toggle
  const [minimapVisible, setMinimapVisible] = useState(() => {
    return localStorage.getItem('iiq.workflow-builder.minimap.v1') !== '0';
  });
  useEffect(() => {
    localStorage.setItem('iiq.workflow-builder.minimap.v1', minimapVisible ? '1' : '0');
  }, [minimapVisible]);

  // Undo / redo
  const applyHistory = useCallback((snap: { nodes: FlowNode[]; edges: FlowEdge[] }) => {
    setNodes(snap.nodes);
    setEdges(snap.edges);
  }, [setNodes, setEdges]);
  const history = useHistory(nodes as FlowNode[], edges as FlowEdge[], applyHistory);

  // Mobile detection (live)
  const [mobile, setMobile] = useState(isMobile());

  // Sidebar collapse state — persisted to localStorage on desktop, always closed initially on mobile
  const [paletteOpen, setPaletteOpen] = useState(() =>
    isMobile() ? false : readBoolStorage(SIDEBAR_PALETTE_KEY, true)
  );
  const [propsOpen, setPropsOpen] = useState(() =>
    isMobile() ? false : readBoolStorage(SIDEBAR_PROPS_KEY, true)
  );

  useEffect(() => {
    function onResize() {
      const wasMobile = mobile;
      const nowMobile = isMobile();
      setMobile(nowMobile);
      // Switching to mobile? Close both sidebars
      if (nowMobile && !wasMobile) {
        setPaletteOpen(false);
        setPropsOpen(false);
      }
      // Switching back to desktop? Restore from localStorage
      if (!nowMobile && wasMobile) {
        setPaletteOpen(readBoolStorage(SIDEBAR_PALETTE_KEY, true));
        setPropsOpen(readBoolStorage(SIDEBAR_PROPS_KEY, true));
      }
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [mobile]);

  // On mobile, opening one sidebar should close the other (mutually exclusive)
  const handleTogglePalette = useCallback(() => {
    setPaletteOpen(o => {
      const next = !o;
      if (mobile && next) setPropsOpen(false);
      return next;
    });
  }, [mobile]);

  const handleToggleProps = useCallback(() => {
    setPropsOpen(o => {
      const next = !o;
      if (mobile && next) setPaletteOpen(false);
      return next;
    });
  }, [mobile]);

  // Persist sidebar collapsed state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_PALETTE_KEY, paletteOpen ? '1' : '0');
  }, [paletteOpen]);
  useEffect(() => {
    localStorage.setItem(SIDEBAR_PROPS_KEY, propsOpen ? '1' : '0');
  }, [propsOpen]);

  // Open the tutorial automatically on first visit (after loading screen)
  useEffect(() => {
    if (loading) return;
    if (!localStorage.getItem(TUTORIAL_DONE_KEY)) {
      const t = setTimeout(() => setTutorialOpen(true), 350);
      return () => clearTimeout(t);
    }
  }, [loading]);

  // Toast helpers
  const pushToast = useCallback((title: string, sub?: string, kind?: ToastMessage['kind']) => {
    const id = nanoid(8);
    setToasts(t => [...t, { id, title, sub, kind: kind || 'success' }]);
  }, []);
  const dismissToast = useCallback((id: string) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  const selectedNode = nodes.find(n => n.id === selectedNodeId) || null;

  // Mark dirty on real changes
  const handleNodesChange = useCallback(
    (changes: NodeChange<FlowNode>[]) => {
      onNodesChange(changes);
      if (changes.some(c => c.type !== 'select' && c.type !== 'dimensions')) setDirty(true);
    },
    [onNodesChange]
  );
  const handleEdgesChange = useCallback(
    (changes: EdgeChange<FlowEdge>[]) => {
      onEdgesChange(changes);
      if (changes.some(c => c.type !== 'select')) setDirty(true);
    },
    [onEdgesChange]
  );

  const handleConnect = useCallback(
    (conn: Connection) => {
      const isMatch = conn.sourceHandle === 'match';
      const isNoMatch = conn.sourceHandle === 'nomatch';
      const color = isMatch ? '#5cd28a' : isNoMatch ? '#e26a6a' : '#C3A35B';
      const newEdge: Edge = {
        ...conn,
        id: `e-${conn.source}-${conn.target}-${nanoid(4)}`,
        type: 'smoothstep',
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed, color },
        style: { stroke: color, strokeWidth: 2 },
        label: isMatch ? 'match' : isNoMatch ? 'no match' : undefined,
        labelStyle: { fill: color, fontWeight: 700, fontSize: 10 },
        labelBgStyle: { fill: 'rgba(11,9,9,0.8)' },
      };
      setEdges(e => addEdge(newEdge, e));
      setDirty(true);
    },
    [setEdges]
  );

  // Drop from palette
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const kind = e.dataTransfer.getData('application/iiq-node') as NodeKind;
      if (!kind || !rfInstance) return;
      const position = rfInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const newNode: Node<FlowData> = {
        id: nanoid(8),
        type: kind,
        position,
        data: defaultDataFor(kind),
      };
      setNodes(n => [...n, newNode]);
      setSelectedNodeId(newNode.id);
      setDirty(true);
      pushToast('Node added', `${kind} dropped on canvas`, 'info');
      if (mobile) {
        setPropsOpen(true);
        setPaletteOpen(false);
      }
    },
    [rfInstance, setNodes, pushToast, mobile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // Right-click on canvas → open context menu
  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!rfInstance) return;
    const flowPos = rfInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    setCtxMenu({ x: e.clientX, y: e.clientY, flowX: flowPos.x, flowY: flowPos.y });
  }, [rfInstance]);

  const handleAddNodeAt = useCallback((kind: NodeKind, flowX: number, flowY: number) => {
    const newNode: Node<FlowData> = {
      id: nanoid(8),
      type: kind,
      position: { x: flowX, y: flowY },
      data: defaultDataFor(kind),
    };
    setNodes(n => [...n, newNode]);
    setSelectedNodeId(newNode.id);
    setDirty(true);
    pushToast(`${kind} added`, 'Dropped at right-click position', 'info');
  }, [setNodes, pushToast]);

  const handlePasteNode = useCallback(() => {
    if (!clipboard || !ctxMenu) return;
    const newNode: Node<FlowData> = {
      ...clipboard,
      id: nanoid(8),
      position: { x: ctxMenu.flowX, y: ctxMenu.flowY },
    };
    setNodes(n => [...n, newNode]);
    setSelectedNodeId(newNode.id);
    setDirty(true);
    pushToast('Node pasted', clipboard.data.label, 'success');
  }, [clipboard, ctxMenu, setNodes, pushToast]);

  const handleNodeClick = useCallback((_: unknown, node: Node) => {
    setSelectedNodeId(node.id);
    if (mobile) {
      setPropsOpen(true);
      setPaletteOpen(false);
    }
  }, [mobile]);

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const handlePropsUpdate = useCallback(
    (id: string, data: FlowData) => {
      setNodes(ns => ns.map(n => (n.id === id ? { ...n, data } : n)));
      setDirty(true);
    },
    [setNodes]
  );

  const handlePropsDelete = useCallback(
    (id: string) => {
      setNodes(ns => ns.filter(n => n.id !== id));
      setEdges(es => es.filter(e => e.source !== id && e.target !== id));
      setSelectedNodeId(null);
      setDirty(true);
      pushToast('Node deleted', 'and its connections removed', 'warn');
    },
    [setNodes, setEdges, pushToast]
  );

  const validateWorkflow = useCallback((): string[] => {
    const issues: string[] = [];
    const triggers = (nodes as FlowNode[]).filter(n => n.data.kind === 'trigger');
    if (triggers.length === 0) issues.push('No trigger node — workflow will never fire');
    if (triggers.length > 1) issues.push(`${triggers.length} triggers — only the first is guaranteed to fire`);
    const reachable = new Set<string>();
    const queue = triggers.map(t => t.id);
    while (queue.length) {
      const id = queue.shift()!;
      if (reachable.has(id)) continue;
      reachable.add(id);
      (edges as FlowEdge[]).filter(e => e.source === id).forEach(e => queue.push(e.target));
    }
    const orphans = (nodes as FlowNode[]).filter(n => !reachable.has(n.id));
    if (orphans.length > 0) issues.push(`${orphans.length} disconnected node${orphans.length === 1 ? '' : 's'} won't run`);
    return issues;
  }, [nodes, edges]);

  const handleSave = useCallback(() => {
    setSaving(true);
    const next: Workflow = {
      ...workflow,
      nodes: nodes as FlowNode[],
      edges: edges as FlowEdge[],
    };
    // Briefly delay so the spinner is visible (saves are sync to localStorage)
    setTimeout(() => {
      saveWorkflow(next);
      setWorkflow(next);
      setActiveWorkflowId(next.id);
      setDirty(false);
      setLastSaved(Date.now());
      setSaving(false);
      const issues = validateWorkflow();
      if (issues.length) {
        pushToast('Saved with warnings', issues.join(' · '), 'warn');
      } else {
        pushToast('Workflow saved', `"${next.name}" · ${nodes.length} nodes · ${edges.length} edges`, 'success');
      }
    }, 320);
  }, [workflow, nodes, edges, pushToast, validateWorkflow]);

  const handleSwitchWorkflow = useCallback(async (wf: Workflow) => {
    if (dirty) {
      const ok = await confirm({
        title: `Switch to "${wf.name}"?`,
        body: 'Your current canvas has unsaved changes. Switching will discard them.',
        confirmLabel: 'Switch anyway',
        cancelLabel: 'Stay here',
        kind: 'warning',
        icon: 'reload',
      });
      if (!ok) return;
    }
    setWorkflow(wf);
    setNodes(wf.nodes);
    setEdges(wf.edges);
    setSelectedNodeId(null);
    setActiveWorkflowId(wf.id);
    setDirty(false);
    setLastSaved(wf.updatedAt);
    pushToast('Switched workflow', `"${wf.name}" · ${wf.nodes.length} blocks`, 'success');
    setTimeout(() => rfInstance?.fitView({ padding: 0.2, duration: 500 }), 100);
  }, [dirty, confirm, setNodes, setEdges, pushToast, rfInstance]);

  const handleNewWorkflow = useCallback(async () => {
    if (dirty) {
      const ok = await confirm({
        title: 'Start a new workflow?',
        body: 'Your current canvas has unsaved changes. Creating a new workflow will discard them.',
        confirmLabel: 'Start new',
        cancelLabel: 'Keep current',
        kind: 'warning',
        icon: 'plus',
      });
      if (!ok) return;
    }
    const fresh = emptyWorkflow();
    setWorkflow(fresh);
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setDirty(true);
    pushToast('New workflow', 'Blank canvas ready', 'info');
  }, [dirty, confirm, setNodes, setEdges, pushToast]);

  const handleDeleteWorkflow = useCallback((id: string) => {
    pushToast('Workflow deleted', 'Removed from local storage', 'warn');
    if (id === workflow.id) {
      // The current one was deleted — start a fresh blank workflow
      const fresh = emptyWorkflow();
      setWorkflow(fresh);
      setNodes([]);
      setEdges([]);
      setSelectedNodeId(null);
      setDirty(true);
    }
  }, [workflow.id, setNodes, setEdges, pushToast]);

  const handleRename = useCallback((name: string) => {
    setWorkflow(w => ({ ...w, name }));
    setDirty(true);
  }, []);

  const handleToggleActive = useCallback(async () => {
    const issues = validateWorkflow();
    if (!workflow.active && issues.length) {
      const ok = await confirm({
        title: `Activate with ${issues.length} warning${issues.length === 1 ? '' : 's'}?`,
        body: `This workflow has issues that may cause it to misbehave when fired on real events:\n\n• ${issues.join('\n• ')}\n\nActivate anyway?`,
        confirmLabel: 'Activate anyway',
        cancelLabel: 'Fix issues first',
        kind: 'warning',
        icon: 'bell',
      });
      if (!ok) return;
    }
    setWorkflow(w => ({ ...w, active: !w.active }));
    setDirty(true);
    pushToast(
      workflow.active ? 'Paused' : 'Active',
      workflow.active ? 'Workflow will not fire on real events' : 'Workflow will fire on every matching event',
      workflow.active ? 'warn' : 'success'
    );
  }, [workflow.active, pushToast, validateWorkflow]);

  const handleClear = useCallback(async () => {
    const ok = await confirm({
      title: 'Clear the whole canvas?',
      body: 'This removes every block and connection. Save first if you want to keep this workflow.',
      confirmLabel: 'Clear everything',
      cancelLabel: 'Keep editing',
      kind: 'destructive',
      icon: 'trash',
    });
    if (!ok) return;
    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setDirty(true);
    pushToast('Canvas cleared', 'All nodes and edges removed', 'warn');
  }, [setNodes, setEdges, pushToast, confirm]);

  const handleAddTemplate = useCallback(
    async (tplFn: () => Workflow) => {
      const tpl = tplFn();
      if (dirty) {
        const ok = await confirm({
          title: `Load the "${tpl.name}" template?`,
          body: 'Your current canvas has unsaved changes. Loading this template will replace everything on the canvas.',
          confirmLabel: 'Replace canvas',
          cancelLabel: 'Keep my work',
          kind: 'warning',
          icon: 'reload',
        });
        if (!ok) return;
      }
      setWorkflow(tpl);
      setNodes(tpl.nodes);
      setEdges(tpl.edges);
      setSelectedNodeId(null);
      setDirty(true);
      pushToast('Template loaded', `${tpl.name} · ${tpl.nodes.length} nodes`, 'info');
      setTimeout(() => rfInstance?.fitView({ padding: 0.2, duration: 600 }), 80);
    },
    [dirty, setNodes, setEdges, pushToast, rfInstance, confirm]
  );

  const handleExport = useCallback(() => {
    const json = exportWorkflow({
      ...workflow,
      nodes: nodes as FlowNode[],
      edges: edges as FlowEdge[],
    });
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${workflow.name.replace(/[^a-z0-9-_]+/gi, '_').toLowerCase()}.workflow.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    pushToast('Exported', `${a.download}`, 'success');
  }, [workflow, nodes, edges, pushToast]);

  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const wf = importWorkflow(text);
        setWorkflow(wf);
        setNodes(wf.nodes);
        setEdges(wf.edges);
        setSelectedNodeId(null);
        setDirty(true);
        pushToast('Imported', `${wf.name} · ${wf.nodes.length} nodes`, 'success');
      } catch (err) {
        pushToast('Import failed', err instanceof Error ? err.message : 'Invalid JSON', 'error');
      }
    };
    input.click();
  }, [setNodes, setEdges, pushToast]);

  const fireNodeAnimation = useCallback((nodeId: string) => {
    const el = document.querySelector(`[data-node-id="${nodeId}"]`);
    if (!el) return;
    el.classList.add('firing');
    setTimeout(() => el.classList.remove('firing'), 1200);
  }, []);

  const handleTestRun = useCallback(async () => {
    setTestVisible(true);
    setTestEvents([]);

    const triggers = (nodes as FlowNode[]).filter(n => n.data.kind === 'trigger');
    if (triggers.length === 0) {
      pushToast('No trigger to start from', 'Add a Trigger node first', 'warn');
      return;
    }

    pushToast('Test run started', `Simulating "${workflow.name}" against fake data`, 'info');
    const runStart = Date.now();
    const runEvents: TestRunEvent[] = [];

    const seen = new Set<string>();
    const queue: { id: string; pathLabel?: string }[] = triggers.map(t => ({ id: t.id }));
    // Context for merge tags / AI outputs
    const ctx: Record<string, unknown> = {
      'customer.first_name': 'Armando',
      'customer.last_name': 'Jimenez',
      'customer.phone': '(469) 316-0530',
      'customer.email': 'armando.j@gmail.com',
      'customer.last_serviced_months': 14,
      'customer.sms_opt_in': true,
      'invoice.number': '2412',
      'invoice.balance': 1200,
      'invoice.balance_due': 1200,
      'storm.cell_size': 2.18,
      'storm.cell_size_in': 2.18,
    };

    while (queue.length > 0) {
      const { id, pathLabel } = queue.shift()!;
      if (seen.has(id)) continue;
      seen.add(id);
      const node = (nodes as FlowNode[]).find(n => n.id === id);
      if (!node) continue;
      const d = node.data;
      const label = d.label;

      fireNodeAnimation(node.id);
      await new Promise(r => setTimeout(r, 380));

      let status: TestRunEvent['status'] = 'fired';
      let message: string | undefined;

      if (d.kind === 'trigger') {
        status = 'fired';
        message = `Source: ${d.source}`;
      } else if (d.kind === 'wait') {
        status = 'waiting';
        message = `Pausing ${d.amount} ${d.unit}…`;
      } else if (d.kind === 'condition') {
        // Use the context if possible, otherwise simulate
        const fieldVal = ctx[d.field];
        let match: boolean;
        if (fieldVal !== undefined) {
          const num = typeof fieldVal === 'number' ? fieldVal : parseFloat(String(fieldVal));
          const target = parseFloat(d.value);
          if (d.operator === 'equals') match = String(fieldVal) === d.value;
          else if (d.operator === 'not_equals') match = String(fieldVal) !== d.value;
          else if (d.operator === 'gt') match = num > target;
          else if (d.operator === 'lt') match = num < target;
          else if (d.operator === 'gte') match = num >= target;
          else if (d.operator === 'lte') match = num <= target;
          else if (d.operator === 'contains') match = String(fieldVal).includes(d.value);
          else if (d.operator === 'starts_with') match = String(fieldVal).startsWith(d.value);
          else match = Math.random() < 0.7;
        } else {
          match = Math.random() < 0.7;
        }
        status = match ? 'matched' : 'skipped';
        message = match
          ? `${d.field} ${d.operator} ${d.value} → true`
          : `${d.field} ${d.operator} ${d.value} → false`;
      } else if (d.kind === 'action') {
        status = 'fired';
        const interpolated = (d.body || '').replace(/\{\{([^}]+)\}\}/g, (_, tag) => String(ctx[tag.trim()] ?? `{{${tag.trim()}}}`));
        const preview = interpolated ? `"${interpolated.slice(0, 80)}${interpolated.length > 80 ? '…' : ''}"` : d.type;
        message = `→ ${d.to || ''} · ${preview}`;
      } else if (d.kind === 'ai_smart') {
        // Actually call the LLM!
        status = 'fired';
        const interpolated = d.prompt.replace(/\{\{([^}]+)\}\}/g, (_, tag) => String(ctx[tag.trim()] ?? `{{${tag.trim()}}}`));
        try {
          const output = await runAiSmart(interpolated, d.model, d.format, ctx);
          if (d.storeAs) ctx[d.storeAs] = output;
          message = `AI · ${output.slice(0, 120)}${output.length > 120 ? '…' : ''}`;
        } catch (err) {
          status = 'failed';
          message = `AI call failed: ${err instanceof Error ? err.message : 'unknown'}`;
        }
      }

      const evt: TestRunEvent = {
        id: nanoid(6),
        nodeId: node.id,
        nodeLabel: pathLabel ? `${label}  (${pathLabel})` : label,
        kind: d.kind,
        status,
        message,
        ts: Date.now(),
      };
      runEvents.push(evt);
      setTestEvents(evts => [...evts, evt]);

      const outgoing = (edges as FlowEdge[]).filter(e => e.source === id);
      for (const e of outgoing) {
        if (d.kind === 'condition') {
          const isMatchBranch = e.sourceHandle === 'match' || e.label === 'match';
          if (status === 'matched' && !isMatchBranch) continue;
          if (status === 'skipped' && isMatchBranch) continue;
        }
        queue.push({ id: e.target, pathLabel: typeof e.label === 'string' ? e.label : undefined });
      }
    }

    pushToast('Test run complete', `${seen.size} nodes traced`, 'success');

    // Persist to history
    const failedCount = runEvents.filter(e => e.status === 'failed').length;
    const skippedCount = runEvents.filter(e => e.status === 'skipped').length;
    const outcome = failedCount > 0 ? 'failed' : skippedCount > 0 ? 'partial' : 'success';
    recordRun({
      id: nano(8),
      workflowId: workflow.id,
      workflowName: workflow.name,
      startedAt: runStart,
      finishedAt: Date.now(),
      events: runEvents,
      outcome,
      nodesFired: runEvents.length,
      durationMs: Date.now() - runStart,
    });
    setHistoryKey(k => k + 1);
  }, [nodes, edges, workflow.name, workflow.id, pushToast, fireNodeAnimation]);

  const openTutorial = useCallback(() => {
    setTutorialOpen(true);
  }, []);

  const closeTutorial = useCallback((completed: boolean) => {
    setTutorialOpen(false);
    if (completed) {
      localStorage.setItem(TUTORIAL_DONE_KEY, '1');
      pushToast('Tutorial complete', 'You can replay it anytime via the ? button', 'success');
    } else {
      pushToast('Tutorial skipped', 'Replay anytime via the ? button', 'info');
    }
    // Reset canvas view
    setTimeout(() => rfInstance?.fitView({ padding: 0.2, duration: 500 }), 60);
  }, [rfInstance, pushToast]);

  const handleSnapZoom = useCallback(() => {
    if (!rfInstance) return;
    rfInstance.fitView({ padding: 0.2, duration: 500, maxZoom: 1.0, minZoom: 0.3 });
    pushToast('Snapped to fit', `${nodes.length} blocks · centered`, 'info');
  }, [rfInstance, pushToast, nodes.length]);

  const handleZoomFar = useCallback(() => {
    if (!rfInstance) return;
    rfInstance.fitView({ padding: 0.4, duration: 500, maxZoom: 0.55 });
    pushToast('Bird\'s-eye view', 'Full canvas · zoomed out', 'info');
  }, [rfInstance, pushToast]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const inEditable = (e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA';
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if (e.key === 'f' && !inEditable && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleSnapZoom();
      }
      if (e.key === 'z' && !inEditable && !e.metaKey && !e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        handleZoomFar();
      }
      // Node search · Cmd/Ctrl+F
      if ((e.metaKey || e.ctrlKey) && e.key === 'f' && !inEditable) {
        e.preventDefault();
        setSearchOpen(true);
      }
      // Toggle minimap · M
      if (e.key === 'm' && !inEditable && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setMinimapVisible(v => !v);
      }
      // Undo / redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const snap = history.undo();
        if (snap) pushToast('Undid', `Reverted ${snap.nodes.length} nodes`, 'info');
        else pushToast('Nothing to undo', 'You\'re at the start of history', 'warn');
      }
      if (((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) ||
          ((e.metaKey || e.ctrlKey) && e.key === 'y')) {
        e.preventDefault();
        const snap = history.redo();
        if (snap) pushToast('Redid', `Re-applied changes`, 'info');
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && !inEditable && selectedNodeId) {
        const node = (nodes as FlowNode[]).find(n => n.id === selectedNodeId);
        if (node) {
          setClipboard(node);
          pushToast('Node copied', `${node.data.label} · Ctrl+V to paste anywhere`, 'info');
        }
      }
      if (e.key === '?' && !inEditable) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
      if (e.key === 'F1' && !inEditable) {
        e.preventDefault();
        openTutorial();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleSave, openTutorial, handleSnapZoom, handleZoomFar, selectedNodeId, nodes, pushToast]);

  // Per-node validation issues (orphans, missing fields, etc.) — also attach to node data for the badge
  const nodeIssues = useMemo(() => validateNodes(nodes as FlowNode[], edges as FlowEdge[]), [nodes, edges]);
  const decoratedNodes = useMemo(() => {
    return (nodes as FlowNode[]).map(n => {
      const issues = nodeIssues.get(n.id) || [];
      // Inject issue list into node data so the node component can render a badge.
      // We only override if changed, to avoid reference churn.
      const prev = (n.data as any).__issues;
      const issuesSig = JSON.stringify(issues.map(i => i.message));
      const prevSig = JSON.stringify((prev || []).map((i: any) => i.message));
      if (issuesSig === prevSig) return n;
      return { ...n, data: { ...n.data, __issues: issues } as any };
    });
  }, [nodes, nodeIssues]);

  // Build tutorial steps with the actual node IDs from the loaded template
  const tutorialSteps = useMemo(() => {
    const trig = (nodes as FlowNode[]).find(n => n.data.kind === 'trigger')?.id;
    const cond = (nodes as FlowNode[]).find(n => n.data.kind === 'condition')?.id;
    const act = (nodes as FlowNode[]).find(n => n.data.kind === 'action')?.id;
    const wait = (nodes as FlowNode[]).find(n => n.data.kind === 'wait')?.id;
    return buildTutorialSteps(trig, cond, act, wait);
  }, [nodes]);

  // App class based on collapsed state
  const appClass = [
    'app',
    paletteOpen ? '' : 'palette-collapsed',
    propsOpen ? '' : 'props-collapsed',
    mobile ? 'is-mobile' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {loading && <LoadingScreen onDone={() => setLoading(false)} />}

      <div className={appClass}>
        <Toolbar
          workflow={workflow}
          onRename={handleRename}
          onToggleActive={handleToggleActive}
          onSave={handleSave}
          onTestRun={handleTestRun}
          onClear={handleClear}
          onExport={handleExport}
          onImport={handleImport}
          onTutorial={openTutorial}
          onTogglePalette={handleTogglePalette}
          onToggleProps={handleToggleProps}
          onSwitchWorkflow={handleSwitchWorkflow}
          onNewWorkflow={handleNewWorkflow}
          onDeleteWorkflow={handleDeleteWorkflow}
          paletteOpen={paletteOpen}
          propsOpen={propsOpen}
          dirty={dirty}
          saving={saving}
          lastSaved={lastSaved}
          theme={theme}
          onToggleTheme={toggleTheme}
        />

        {paletteOpen ? (
          <Palette onAddTemplate={handleAddTemplate} />
        ) : (
          <button
            className="sidebar-stub left"
            onClick={() => setPaletteOpen(true)}
            title="Show palette"
            aria-label="Show palette"
          >
            <span>⟩</span>
            <span className="stub-label">Palette</span>
          </button>
        )}

        <div
          className="canvas"
          ref={wrapperRef}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onContextMenu={handleContextMenu}
          data-tour="canvas"
        >
          <CanvasBackground />
          {nodes.length === 0 && (
            <EmptyCanvas
              onBuildWithAI={() => setAssistantOpen(true)}
              onLoadTemplate={() => setPaletteOpen(true)}
              onAddTrigger={() => {
                if (!rfInstance) return;
                const c = rfInstance.screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
                handleAddNodeAt('trigger', c.x - 140, c.y - 60);
              }}
            />
          )}
          <ReactFlow
            nodes={decoratedNodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={handleConnect}
            onNodeClick={handleNodeClick}
            onPaneClick={handlePaneClick}
            onInit={setRfInstance}
            nodeTypes={NODE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.2, maxZoom: 1.0 }}
            minZoom={0.1}
            maxZoom={2.5}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: true,
              markerEnd: { type: MarkerType.ArrowClosed, color: '#C3A35B' },
              style: { stroke: '#C3A35B', strokeWidth: 2 },
            }}
            deleteKeyCode={['Backspace', 'Delete']}
            proOptions={{ hideAttribution: true }}
          >
            <Controls showInteractive={false} showZoom={false} showFitView={false} />
            <ZoomWidget
              onSnap={handleSnapZoom}
              onZoomFar={handleZoomFar}
              onZoomIn={() => rfInstance?.zoomIn({ duration: 300 })}
              onZoomOut={() => rfInstance?.zoomOut({ duration: 300 })}
            />
            {!mobile && minimapVisible && (
              <MiniMap
                nodeColor={n => {
                  const t = n.type as string;
                  if (t === 'trigger')   return '#6ec6ff';
                  if (t === 'condition') return '#ffd07a';
                  if (t === 'action')    return '#5cd28a';
                  if (t === 'ai_smart')  return '#d4b975';
                  return 'rgba(247,247,245,0.4)';
                }}
                nodeStrokeColor="rgba(11,9,9,0.8)"
                maskColor="rgba(11,9,9,0.6)"
                pannable
                zoomable
              />
            )}
          </ReactFlow>
        </div>

        {propsOpen ? (
          <PropertiesPanel node={selectedNode} onUpdate={handlePropsUpdate} onDelete={handlePropsDelete} />
        ) : (
          <button
            className="sidebar-stub right"
            onClick={() => setPropsOpen(true)}
            title="Show properties"
            aria-label="Show properties"
          >
            <span>⟨</span>
            <span className="stub-label">Edit</span>
            {selectedNodeId && <span className="stub-dot" />}
          </button>
        )}

        {mobile && (
          <MobileBottomNav
            paletteOpen={paletteOpen}
            propsOpen={propsOpen}
            testOpen={testVisible}
            onTogglePalette={handleTogglePalette}
            onToggleProps={handleToggleProps}
            onToggleTest={() => setTestVisible(v => !v)}
            onTutorial={openTutorial}
            hasSelection={!!selectedNodeId}
          />
        )}

        <TestRunDrawer events={testEvents} visible={testVisible} onClose={() => setTestVisible(false)} refreshKey={historyKey} />
        <Toasts messages={toasts} onDismiss={dismissToast} />

        <Tutorial
          steps={tutorialSteps}
          open={tutorialOpen}
          onClose={closeTutorial}
          rfInstance={rfInstance}
          paletteOpen={paletteOpen}
          propsOpen={propsOpen}
          onRequestPalette={setPaletteOpen}
          onRequestProps={setPropsOpen}
        />

        {/* AI Assistant FAB + drawer — also visible during tutorial so its step can spotlight it */}
        {!assistantOpen && !loading && (
          <div className={`assistant-fab-wrap ${fabExpanded ? 'expanded' : 'compact'}`}>
            <button
              className="assistant-fab"
              onClick={() => setAssistantOpen(true)}
              title="AI Workflow Assistant · also available on right-click"
            >
              <span className="fab-orb" aria-hidden="true">
                <span className="fab-orb-ring" />
                <span className="fab-orb-ring delayed" />
                <span className="fab-orb-core">✨</span>
              </span>
              {fabExpanded && (
                <>
                  <span className="fab-text">
                    <b>Build with AI</b>
                    <span className="fab-sub">Describe it · we'll build it</span>
                  </span>
                  <span className="fab-arrow">→</span>
                </>
              )}
            </button>
            <button
              className="fab-resize-toggle"
              onClick={() => setFabExpanded(e => !e)}
              title={fabExpanded ? 'Collapse to icon' : 'Expand label'}
              aria-label={fabExpanded ? 'Make FAB compact' : 'Expand FAB'}
            >
              {fabExpanded ? '−' : '+'}
            </button>
          </div>
        )}
        {/* Right-click context menu */}
        <ContextMenu
          open={!!ctxMenu}
          pos={ctxMenu}
          onClose={() => setCtxMenu(null)}
          onAddNode={handleAddNodeAt}
          onBuildWithAI={() => setAssistantOpen(true)}
          onClear={handleClear}
          onPaste={handlePasteNode}
          hasClipboard={!!clipboard}
        />

        <AssistantChat
          open={assistantOpen}
          onClose={() => setAssistantOpen(false)}
          onApplyWorkflow={async wf => {
            if (dirty) {
              const ok = await confirm({
                title: 'Load the AI-generated workflow?',
                body: 'Your current canvas has unsaved changes. Loading the AI build will replace everything here.',
                confirmLabel: 'Load AI workflow',
                cancelLabel: 'Keep my work',
                kind: 'gold',
                icon: 'sparkle',
              });
              if (!ok) return;
            }
            setWorkflow(wf);
            setNodes(wf.nodes);
            setEdges(wf.edges);
            setSelectedNodeId(null);
            setDirty(true);
            pushToast('AI workflow loaded', wf.name, 'success');
            setTimeout(() => rfInstance?.fitView({ padding: 0.2, duration: 600 }), 80);
          }}
        />

        <ConfirmDialog request={confirmReq} onResolve={resolveConfirm} />
        <ShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
        <NodeSearch
          open={searchOpen}
          nodes={nodes as FlowNode[]}
          onClose={() => setSearchOpen(false)}
          onSelect={id => setSelectedNodeId(id)}
          rfInstance={rfInstance}
        />
      </div>
    </>
  );
}

export default function App() {
  return (
    <ReactFlowProvider>
      <AppInner />
    </ReactFlowProvider>
  );
}
